/**
 * Renders the account information shown in the top-right corner of the page.
 * It allows to access the profile page and to sign-out from the application.
 */

import React, {useContext} from 'react'
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import {NavLink} from "react-router-dom";
import {Button, Card, CardHeader, Divider, IconButton, Typography} from '@mui/material';

import {FirebaseAuthContext} from 'components/commons/firebase/FirebaseAuthProvider';
import {firebaseService} from 'components/commons/firebase/Firebase';
import {useStyles} from './AccountInfo.css'
import {Edit, ExitToApp} from "@mui/icons-material";
import UserProfilePicture from "../../admin/sections/users/overview/UserProfilePicture";
import {useIntl} from "react-intl";

const AccountInfo = () => {
    // State required for handling the context menu shown when clicking on the account info button
    const [anchorEl, setAnchorEl] = React.useState(null);

    // The current user, which holds the information to be displayed
    const {signedInUser, clearUserData, signedInUserData} = useContext(FirebaseAuthContext)

    const classes = useStyles()

    // Internationalization for simple strings
    const intl = useIntl();

    // Handles the click on the account info button
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    // Handles the closing of the context menu
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Handles the click on 'Sign-out' in the context menu
    const handleLogout = () => {
        setAnchorEl(null);
        clearUserData()
        firebaseService.auth().signOut()
    };

    const getAccountInfoMainTitle = () => {
        // The signedInUser can be temporarily null
        // Although the redirection to the dashboard is done if the signed-in user is loaded, the account info is shown already before
        return <Typography>
            {signedInUserData?.firstName && signedInUserData?.secondName
                ? signedInUserData?.firstName + ' ' + signedInUserData?.secondName
                : signedInUserData?.displayName}
        </Typography>
    }

    const getAccountInfoSubTitle = () => {
        return <div>
            {signedInUserData?.email}
        </div>
    }

    return (
        <div>
            {/* Profile info text and button */}
            <div className={classes.profileInfo}>
                <span>
                  <p className={classes.userInfoText}>{signedInUser.displayName}</p>
                  <p className={classes.providerInfoText}>
                      {intl.formatMessage({
                          id: 'app.profile.info.accessedThrough',
                          defaultMessage: 'Accessed through {signInProvider}'
                      }, {
                          signInProvider: signedInUser.providerData[0]?.providerId
                              ? signedInUser.providerData[0].providerId
                              : 'Moodle'
                      })}
                  </p>
                </span>
                <Button onClick={handleClick}>
                    <Avatar title={signedInUser.displayName}
                            className={classes.orange}>{signedInUser.displayName?.charAt(0)}</Avatar>
                </Button>
            </div>

            {/* Context menu when clicked on the account info button */}
            <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}>
                <Card className={classes.card}>
                    <CardHeader
                        avatar={
                            <UserProfilePicture user={signedInUserData} height={40} width={40}/>
                        }
                        title={getAccountInfoMainTitle()}
                        subheader={getAccountInfoSubTitle()}
                    />
                    <Divider/>
                    <div className={classes.actions}>
                        <IconButton
                            component={NavLink}
                            to='/common/profile'
                            onClick={() => setAnchorEl(null)}
                            size="large">
                            <Edit className={classes.editIcon}/>
                        </IconButton>
                        <IconButton onClick={handleLogout} size="large">
                            <ExitToApp className={classes.logoutIcon}/>
                        </IconButton>
                    </div>
                </Card>
            </Menu>
        </div>
    );
}

export default AccountInfo
